import { useCallback, useMemo, useRef, useState, type ReactNode } from "react";

import type { RoleWithProject } from "../api/types";
import { data } from "../data";
import { AssistantContext, type Hit, type Turn } from "./assistant";

/**
 * One conversation, above the two panels that show it.
 *
 * The dashboard draws the assistant in its right rail and every other screen
 * draws it in the dock, which are two components rather than one moving. So
 * opening a production from an answer and coming back did not lose the
 * conversation, it showed a different one, and each of them separately read
 * every production in the database to be able to answer at all.
 *
 * Holding both here means the thread survives navigation because it never
 * belonged to a screen, and the productions are read once.
 */
export function AssistantProvider({ children }: { children: ReactNode }) {
  const [hits, setHits] = useState<Hit[]>([]);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [connected, setConnected] = useState<boolean | null>(null);
  const done = useRef(false);
  const running = useRef(false);

  const ensure = useCallback(() => {
    if (done.current || running.current) return;
    running.current = true;
    setLoading(true);
    setFailed(false);

    // Asked once, beside the productions. Separate from them on purpose: the
    // panel is usable when this fails, because not knowing whether a model is
    // there is the same as there not being one.
    void data
      .chatStatus()
      .then((status) => setConnected(status.connected))
      .catch(() => setConnected(false));

    void (async () => {
      try {
        const roles: RoleWithProject[] = [];
        let offset = 0;
        for (;;) {
          const page = await data.listRoles({ limit: 200, offset, q: undefined });
          roles.push(...page.items);
          // Stop on an empty page as well as on has_more, so a server that
          // disagrees with itself cannot spin this forever.
          if (!page.has_more || page.items.length === 0) break;
          offset += page.items.length;
        }

        const seen = new Set<string>();
        const found: Hit[] = [];
        for (const role of roles) {
          if (seen.has(role.project.id)) continue;
          seen.add(role.project.id);
          found.push({ project: role.project, roleId: role.id });
        }
        setHits(found);
        // Marked read only once it has been. This used to be set before the
        // request, so one failed page out of ten left the panel holding
        // nothing and certain it had already loaded. Every question after
        // that answered "nothing matches", which is indistinguishable from an
        // honest miss, and only a hard reload cleared it.
        done.current = true;
      } catch {
        setFailed(true);
      } finally {
        running.current = false;
        setLoading(false);
      }
    })();
  }, []);

  const value = useMemo(
    () => ({ hits, loading, failed, ensure, turns, setTurns, connected }),
    [hits, loading, failed, ensure, turns, connected],
  );

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
}
